/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { Button, Icon, SecurityHelper, useFlags, useToast } from 'sarsaparilla';
import AddDiscountButton from './AddDiscountButton';
import AddDiscountForm from './AddDiscountForm';
import { setCart } from '../../actions/cartActions';

type AddDiscountProps = {
    hasDivider?: boolean;
};

type ReduxState = {
    cart: {
        reservations: {
            cart_line_items: {
                sku: string;
            }[];
        }[];
    };
};

export default function AddDiscount({ hasDivider = false }: AddDiscountProps) {
    const dispatch = useDispatch();
    const cartReservations = useSelector((state: ReduxState) => state.cart.reservations);
    const { enablePromoDiscountCodes } = useFlags();
    const showToast = useToast();
    const [isShowingDiscountForm, setIsShowingDiscountForm] =
        React.useState<boolean>(false);
    const [isRemovingDiscounts, setIsRemovingDiscounts] = React.useState<boolean>(false);

    const displayRemoveDiscountsButton = React.useMemo(
        () =>
            !!cartReservations?.find((reservation) => {
                return !!reservation.cart_line_items?.find((lineItem) =>
                    lineItem.sku.startsWith('PROMO')
                );
            }),
        [cartReservations]
    );

    const toggleIsShowingDiscountForm = () => {
        setIsShowingDiscountForm((flag) => !flag);
    };

    const handleRemoveDiscountsClicked = () => {
        setIsRemovingDiscounts(true);
        axios
            .delete('/api/cart/checkout/promocode', {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then(({ data }) => {
                dispatch(setCart(data));
                showToast('Discount codes successfully removed.', { type: 'success' });
            })
            .catch(() => {
                showToast('There was an issue trying to remove your discount codes.', {
                    type: 'error',
                });
            })
            .finally(() => {
                setIsRemovingDiscounts(false);
            });
    };

    if (!enablePromoDiscountCodes || !process.env.PROMO_DISCOUNT_CODES_ENABLED) {
        return null;
    }

    return (
        <div className="add-discount-wrapper">
            {!isShowingDiscountForm && (
                <AddDiscountButton handleClick={toggleIsShowingDiscountForm} />
            )}

            {isShowingDiscountForm && (
                <AddDiscountForm
                    resetAddDiscount={toggleIsShowingDiscountForm}
                    hasDivider={hasDivider}
                />
            )}

            {displayRemoveDiscountsButton && (
                <Button
                    shouldFitContainer
                    appearance="link"
                    iconBeforeElement={<Icon iconName="remove-circle" />}
                    onClick={handleRemoveDiscountsClicked}
                    isLoading={isRemovingDiscounts}
                >
                    Remove Discounts
                </Button>
            )}
        </div>
    );
}

// cSpell:ignore promocode
